/**
 * ARCHIVO GENERADO — no editar.
 *
 * Los valores salen del bloque `theme` de la configuración del proyecto.
 * Para cambiar la apariencia se edita esa configuración, no este archivo; lo
 * que se escriba acá se pierde en la próxima regeneración.
 *
 * Para ajustes que la configuración no pueda expresar, está `identity.ts`.
 *
 * Preset activo: legacy
 */

export interface Surface {
  bg: string;
  on: string;
  onMuted: string;
  border: string;
  hover: string;
  subtle?: string;
  /** El texto sobre `subtle`: el tinte se lee con su propio ink. */
  onSubtle?: string;
  borderStrong?: string;
}

/**
 * Los primitivos, con tipo DECLARADO y no inferido.
 *
 * Sin esto TypeScript infiere el tipo de cada valor serializado: una lista
 * vacía pasa a ser `never[]` y no admite que se le agregue nada, y el tema en
 * pruebas —que sí la trae poblada— deja de asignar. Los datos vienen del
 * generador; su forma se declara acá.
 */
export interface Primitivos {
  scales: Record<string, Record<string, string>>;
  spacingUnit: number;
  spacing: Record<string, number>;
  radius: Record<string, number>;
  radiusProgression: "even" | "growing";
  typography: { fontFamily: string; weights: number[] } & Record<string, any>;
  /**
   * Los ingredientes sueltos de `typography` — tamaño, altura de línea y peso
   * por nombre de token, sin nivel semántico. Ausente cuando el preset no
   * tiene una escala de origen (ver `TypographyIngredients` en el generador).
   */
  typographyIngredients?: {
    fontSize: Record<string, string>;
    lineHeight: Record<string, number>;
    fontWeight: Record<string, number>;
  };
  elevation: {
    doctrine: "flat" | "layered";
    levels: number;
    shadows: string[];
  };
  gradients: { surface: string | null; emphasis: string | null };
  motion: {
    duration: Record<string, number>;
    easing: Record<string, string>;
    neverAnimate: string[];
    respectReducedMotion: boolean;
  };
  focus: { width: number; offset: number; style: "solid" | "dashed" };
  /**
   * El grosor de las líneas del tema: bordes y separadores.
   *
   * Opcional porque los presets que transcriben sus valores pueden no declararlo;
   * quien lo lee cae a 1, que es lo que se dibujaba antes de que el primitivo
   * existiera. El anillo de foco va aparte (`focus`): su umbral es de
   * accesibilidad, no de estilo.
   */
  stroke?: { outline: number; focusRing: number };
  icons: {
    family: "material" | "lucide";
    strokeWidth: number;
    size: Record<string, number>;
  };
  layers: Record<string, number>;
  shadows: string[];
  duration: { fast: number; base: number; slow: number };
}

/**
 * Las superficies que pertenecen a una FAMILIA DE COMPONENTE y no a un rol de
 * pantalla: `page`/`raised`/`sunken` dicen a qué altura está algo, esto dice
 * qué clase de cosa es. La línea de una grilla se repite diez veces por
 * pantalla y la de una tarjeta una sola, así que el gris que separa dos bloques
 * satura una tabla — y hasta acá no había forma de ablandar una sin la otra.
 */
export interface FamilySurfaces {
  /** Grilla/tabla: la línea que se repite N veces por pantalla. */
  grid: {
    border: string;
    headBorder: string;
    headBg: string;
    rowHover: string;
    /** El fondo de las filas pares, cuando la grilla se separa por rayado. */
    rowAlt: string;
  };
  /**
   * Campo de formulario, en sus tres estados. Su borde es lo único que
   * identifica al control, por eso 1.4.11 le exige 3:1 y al de una tarjeta no.
   */
  field: { border: string; borderHover: string; borderFocus: string };
}

export interface SemanticSet {
  page: Surface;
  raised: Surface;
  sunken: Surface;
  inverse: Surface;
  primary: Surface;
  secondary: Surface;
  error: Surface;
  warning: Surface;
  success: Surface;
  info?: Surface;
  appBar: Surface;
  sidebar: Surface & { selected: string; onSelected: string };
  /** 6.9: cuatro situaciones, no una. */
  sensitive: {
    masked: Surface;
    revealed: Surface;
    ownership: Surface;
    forbidden: Surface;
  };
  /** Las superficies por familia de componente. */
  families: FamilySurfaces;
  text: { primary: string; secondary: string; disabled?: string };
  divider: string;
  focusRing: string;
}

/**
 * Los diez roles de una capa de contraste. Un componente no conoce un color:
 * conoce `Main`, y qué color es `Main` depende de la capa en la que está.
 */
export interface RolesDeCapa {
  Canvas: string;
  Content: string;
  Main: string;
  On_Main: string;
  Surface: string;
  On_Surface: string;
  Mantle: string;
  Border: string;
  Content_Low: string;
  Focus: string;
}

export interface ModoPorCapas {
  contrast: Record<string, RolesDeCapa>;
}

/**
 * El sistema de capas completo, cuando el tema viene de un esquema tokenizado.
 *
 * Existe porque el conjunto semántico de arriba COLAPSA seis capas de contraste
 * en cuatro superficies: sirve para construir la pantalla, pero no alcanza para
 * mostrar el sistema. Esto es lo que se colapsó, sin adaptar, para que la
 * galería no tenga que mentir sobre lo que el tema ofrece.
 *
 * Nulo cuando el tema no viene de un esquema así, que es el caso normal.
 */
export interface CapasMatu {
  /**
   * Qué combinación del esquema es: una marca por un tema. Ausente cuando el
   * tema no salió de una (armado a mano).
   *
   * `brand` presente significa que la marca es propia del proyecto y no una
   * del corpus del esquema: `colorScheme` la nombra como tal, y acá viaja la
   * semilla de la que se derivó.
   *
   * `accent` presente significa que el acento NO sale del mismo esquema que
   * las superficies: viaja tal como se declaró, un nombre del esquema o un
   * color propio.
   *
   * `surfaces` es la DIRECCIÓN en que se lee la escalera de superficies —de
   * cuál de las capas de abajo salen la página, la tarjeta y la barra—. Ausente
   * significa la de siempre.
   */
  selection?: {
    colorScheme: string;
    brand?: string;
    theme: string;
    accent?: string;
    surfaces?: string;
  };
  /**
   * La escala de color del esquema, escalón por escalón: de acá salen las seis
   * capas de abajo, que no son otra cosa que elecciones de escalones.
   *
   * Viaja con las capas y no en `tokens.primitives.scales` porque ese otro
   * campo es el vocabulario de los temas DERIVADOS de una semilla —familias
   * (marca, acento, neutral, los estados) de once escalones numéricos— y este
   * esquema no tiene esa forma: es una sola escala de trece escalones con
   * nombre propio. Forzarla a ese molde inventaría una correspondencia que no
   * existe.
   */
  baseColors: Record<string, string>;
  /**
   * La escala del ACENTO, con el mismo sentido que `accent`: presente solo
   * cuando el acento tiene su propio eje. Ausente ⇒ el acento sale de la escala
   * de arriba.
   */
  accentBaseColors?: Record<string, string>;
  /** Opacidades de los estados (0..1), como las declara el esquema. */
  states: Record<string, number>;
  light: ModoPorCapas;
  dark: ModoPorCapas;
  /**
   * Las capas del ACENTO, cuando es un eje aparte del de las superficies.
   * Ausente —el caso normal— significa que el acento sigue a `light`/`dark`.
   */
  accent?: { light: ModoPorCapas; dark: ModoPorCapas };
  /** Los estados son un eje propio: cada uno con sus capas completas. */
  status: Record<string, { light: ModoPorCapas; dark: ModoPorCapas }>;
}

export const capasMatu: CapasMatu | null = null;

export const tokens = {
  preset: "legacy",
  primitives: {
    scales: {},
    spacingUnit: 8,
    spacing: {
      unit: 8,
      none: 0,
      xs: 4,
      s: 8,
      m: 16,
      l: 24,
      xl: 32,
      xxl: 48,
    },
    radius: {
      none: 0,
      xs: 4,
      s: 6,
      m: 8,
      l: 10,
      xl: 16,
      full: 9999,
    },
    radiusProgression: "even",
    motion: {
      duration: {
        instant: 0,
        fast: 150,
        base: 250,
        slow: 375,
        deliberate: 500,
      },
      easing: {
        standard: "cubic-bezier(0.2, 0, 0, 1)",
        entering: "cubic-bezier(0, 0, 0, 1)",
        exiting: "cubic-bezier(0.3, 0, 1, 1)",
        emphasized: "cubic-bezier(0.3, 0, 0, 1.2)",
      },
      neverAnimate: [],
      respectReducedMotion: false,
    },
    elevation: {
      doctrine: "flat",
      levels: 3,
      shadows: [
        "none",
        "0 1px 2px rgba(0,0,0,0.04)",
        "0 2px 4px rgba(0,0,0,0.06)",
        "0 4px 8px rgba(0,0,0,0.08)",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
        "none",
      ],
    },
    gradients: {
      surface: null,
      emphasis: null,
    },
    focus: {
      width: 2,
      offset: 2,
      style: "solid",
    },
    icons: {
      family: "lucide",
      strokeWidth: 1.75,
      size: {
        s: 18,
        m: 20,
        l: 24,
      },
    },
    shadows: [
      "none",
      "0 1px 2px rgba(0,0,0,0.04)",
      "0 2px 4px rgba(0,0,0,0.06)",
      "0 4px 8px rgba(0,0,0,0.08)",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
      "none",
    ],
    layers: {
      menu: 10250,
      snackbar: 10350,
      tooltip: 10400,
    },
    duration: {
      fast: 150,
      base: 250,
      slow: 375,
    },
    typography: {
      weights: [400, 500, 600, 700],
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      titleFontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      buttonFontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      h1: {
        fontWeight: 600,
        fontSize: "2rem",
        lineHeight: 1.4,
      },
      h2: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.4,
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.25rem",
        lineHeight: 1.5,
      },
      h4: {
        fontWeight: 500,
        fontSize: "1.125rem",
        lineHeight: 1.5,
      },
      h5: {
        fontWeight: 500,
        fontSize: "1rem",
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 500,
        fontSize: "0.875rem",
        lineHeight: 1.6,
      },
      body1: {
        fontWeight: 400,
        fontSize: "0.9375rem",
        lineHeight: 1.7,
      },
      body2: {
        fontWeight: 400,
        fontSize: "0.8125rem",
        lineHeight: 1.6,
      },
      subtitle1: {
        fontWeight: 500,
        lineHeight: 1.6,
      },
      subtitle2: {
        fontWeight: 400,
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 500,
        textTransform: "none",
      },
    },
  } as Primitivos,
  light: {
    page: {
      bg: "#FAFAFA",
      on: "#1A1A1A",
      onMuted: "#6B7280",
      border: "#E5E5E5",
      hover: "#F9FAFB",
    },
    raised: {
      bg: "#FFFFFF",
      on: "#1A1A1A",
      onMuted: "#6B7280",
      border: "#E5E5E5",
      hover: "#F9FAFB",
      borderStrong: "#D1D5DB",
    },
    sunken: {
      bg: "#FAFAFA",
      on: "#1A1A1A",
      onMuted: "#6B7280",
      border: "#F0F0F0",
      hover: "#F9FAFB",
    },
    inverse: {
      bg: "#1A1A1A",
      on: "#FFFFFF",
      onMuted: "#E5E5E5",
      border: "#1A1A1A",
      hover: "#2D2D2D",
    },
    primary: {
      bg: "#2B6CB0",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#1E4A7A",
      hover: "#1E4A7A",
      subtle: "#4A9BD9",
    },
    secondary: {
      bg: "#6B7280",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#6B7280",
      hover: "#6B7280",
    },
    error: {
      bg: "#DC3545",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#DC3545",
      hover: "#DC3545",
    },
    warning: {
      bg: "#E8912D",
      on: "#000000",
      onMuted: "#000000",
      border: "#E8912D",
      hover: "#E8912D",
    },
    success: {
      bg: "#2F9E44",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#2F9E44",
      hover: "#2F9E44",
    },
    appBar: {
      bg: "#FFFFFF",
      on: "#1A1A1A",
      onMuted: "#6B7280",
      border: "#E5E5E5",
      hover: "#F9FAFB",
    },
    sidebar: {
      bg: "#FFFFFF",
      on: "#1A1A1A",
      onMuted: "#6B7280",
      border: "#E5E5E5",
      hover: "#F9FAFB",
      selected: "#2B6CB0",
      onSelected: "#FFFFFF",
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#6B7280",
    },
    divider: "#E5E5E5",
    focusRing: "#2B6CB0",
    sensitive: {
      masked: {
        bg: "#FAFAFA",
        on: "#1A1A1A",
        onMuted: "#6B7280",
        border: "#E5E5E5",
        hover: "#F9FAFB",
      },
      revealed: {
        bg: "#FAFAFA",
        on: "#1A1A1A",
        onMuted: "#6B7280",
        border: "#E5E5E5",
        hover: "#F9FAFB",
      },
      ownership: {
        bg: "#FAFAFA",
        on: "#1A1A1A",
        onMuted: "#6B7280",
        border: "#E5E5E5",
        hover: "#F9FAFB",
      },
      forbidden: {
        bg: "#FAFAFA",
        on: "#1A1A1A",
        onMuted: "#6B7280",
        border: "#E5E5E5",
        hover: "#F9FAFB",
      },
    },
    families: {
      grid: {
        border: "#F0F0F0",
        headBorder: "#E5E5E5",
        headBg: "#FAFAFA",
        rowHover: "#F9FAFB",
        rowAlt: "#FCFDFD",
      },
      field: {
        border: "#E5E5E5",
        borderHover: "#D1D5DB",
        borderFocus: "#2B6CB0",
      },
    },
  } as SemanticSet,
  dark: {
    page: {
      bg: "#1A1A1A",
      on: "#E5E5E5",
      onMuted: "#6B7280",
      border: "#404040",
      hover: "#333",
    },
    raised: {
      bg: "#2D2D2D",
      on: "#E5E5E5",
      onMuted: "#6B7280",
      border: "#404040",
      hover: "#333",
      borderStrong: "#555",
    },
    sunken: {
      bg: "#2D2D2D",
      on: "#E5E5E5",
      onMuted: "#6B7280",
      border: "#333",
      hover: "#333",
    },
    inverse: {
      bg: "#1A1A1A",
      on: "#FFFFFF",
      onMuted: "#E5E5E5",
      border: "#1A1A1A",
      hover: "#2D2D2D",
    },
    primary: {
      bg: "#2B6CB0",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#1E4A7A",
      hover: "#1E4A7A",
      subtle: "#4A9BD9",
    },
    secondary: {
      bg: "#6B7280",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#6B7280",
      hover: "#6B7280",
    },
    error: {
      bg: "#DC3545",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#DC3545",
      hover: "#DC3545",
    },
    warning: {
      bg: "#E8912D",
      on: "#000000",
      onMuted: "#000000",
      border: "#E8912D",
      hover: "#E8912D",
    },
    success: {
      bg: "#2F9E44",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#2F9E44",
      hover: "#2F9E44",
    },
    appBar: {
      bg: "#1A1A1A",
      on: "#E5E5E5",
      onMuted: "#6B7280",
      border: "#404040",
      hover: "#333",
    },
    sidebar: {
      bg: "#2D2D2D",
      on: "#E5E5E5",
      onMuted: "#6B7280",
      border: "#404040",
      hover: "#333",
      selected: "#2B6CB0",
      onSelected: "#FFFFFF",
    },
    text: {
      primary: "#E5E5E5",
      secondary: "#6B7280",
    },
    divider: "#404040",
    focusRing: "#2B6CB0",
    sensitive: {
      masked: {
        bg: "#2D2D2D",
        on: "#E5E5E5",
        onMuted: "#6B7280",
        border: "#404040",
        hover: "#333",
      },
      revealed: {
        bg: "#2D2D2D",
        on: "#E5E5E5",
        onMuted: "#6B7280",
        border: "#404040",
        hover: "#333",
      },
      ownership: {
        bg: "#2D2D2D",
        on: "#E5E5E5",
        onMuted: "#6B7280",
        border: "#404040",
        hover: "#333",
      },
      forbidden: {
        bg: "#2D2D2D",
        on: "#E5E5E5",
        onMuted: "#6B7280",
        border: "#404040",
        hover: "#333",
      },
    },
    families: {
      grid: {
        border: "#333",
        headBorder: "#404040",
        headBg: "#2D2D2D",
        rowHover: "#333",
        rowAlt: "#303030",
      },
      field: {
        border: "#404040",
        borderHover: "#555",
        borderFocus: "#2B6CB0",
      },
    },
  } as SemanticSet,
};

/**
 * El tema en pruebas y quien lo ve. Nulo cuando el proyecto no declara uno, que
 * es el caso normal: todos ven lo mismo.
 */
export const previewTokens: {
  preset: string;
  primitives: Primitivos;
  light: SemanticSet;
  dark: SemanticSet;
  audience: { roles?: string[]; systemOwner?: boolean; tenants?: string[] };
  systemOwnerTenant: string;
} | null = {
  preset: "base",
  primitives: {
    scales: {},
    spacingUnit: 8,
    spacing: {
      unit: 8,
      none: 0,
      xs: 4,
      s: 8,
      m: 16,
      l: 24,
      xl: 32,
      xxl: 48,
    },
    radius: {
      none: 0,
      xs: 2,
      s: 4,
      m: 8,
      l: 12,
      xl: 16,
      full: 999,
    },
    radiusProgression: "growing",
    typography: {
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      titleFontFamily:
        '"IBM Plex Serif", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      buttonFontFamily:
        '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      weights: [400, 500, 600, 700],
      h1: {
        fontWeight: 600,
        fontSize: "2rem",
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.2,
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.25rem",
        lineHeight: 1.3,
      },
      h4: {
        fontWeight: 500,
        fontSize: "1.125rem",
        lineHeight: 1.3,
      },
      h5: {
        fontWeight: 500,
        fontSize: "1rem",
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 500,
        fontSize: "0.875rem",
        lineHeight: 1.5,
      },
      body1: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.7,
      },
      body2: {
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: 1.5,
      },
      subtitle1: {
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontWeight: 400,
        lineHeight: 1.5,
      },
      button: {
        fontWeight: 500,
        textTransform: "none",
      },
    },
    typographyIngredients: {
      fontSize: {
        "text-11": "0.6875rem",
        "text-12": "0.75rem",
        "text-14": "0.875rem",
        "text-16": "1rem",
        "text-18": "1.125rem",
        "text-20": "1.25rem",
        "text-24": "1.5rem",
        "text-28": "1.75rem",
        "text-32": "2rem",
        "text-36": "2.25rem",
        "text-40": "2.5rem",
        "text-44": "2.75rem",
        "text-48": "3rem",
        "text-56": "3.5rem",
        "text-64": "4rem",
        "text-72": "4.5rem",
        "text-80": "5rem",
        "text-96": "6rem",
      },
      lineHeight: {
        "line-height-90": 0.9,
        "line-height-100": 1,
        "line-height-120": 1.2,
        "line-height-130": 1.3,
        "line-height-150": 1.5,
        "line-height-170": 1.7,
      },
      fontWeight: {
        "font-weight-100": 100,
        "font-weight-200": 200,
        "font-weight-300": 300,
        "font-weight-400": 400,
        "font-weight-500": 500,
        "font-weight-600": 600,
        "font-weight-700": 700,
        "font-weight-800": 800,
        "font-weight-900": 900,
      },
    },
    elevation: {
      doctrine: "layered",
      levels: 4,
      shadows: [
        "none",
        "0px 2px 4px 0px #5555554D",
        "0px 4px 4px 0px #5555554D",
        "0px 4px 8px 0px #5555554D",
        "0px 4px 8px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
        "0px 4px 12px 4px #5555554D",
      ],
    },
    gradients: {
      surface: null,
      emphasis: null,
    },
    motion: {
      duration: {
        instant: 0,
        fast: 120,
        base: 180,
        slow: 260,
        deliberate: 360,
      },
      easing: {
        standard: "cubic-bezier(0.2, 0, 0, 1)",
        entering: "cubic-bezier(0, 0, 0, 1)",
        exiting: "cubic-bezier(0.3, 0, 1, 1)",
        emphasized: "cubic-bezier(0.3, 0, 0, 1.2)",
      },
      neverAnimate: [
        "tableRowHover",
        "listItemHover",
        "menuItemHover",
        "checkboxToggle",
        "inputFocus",
      ],
      respectReducedMotion: true,
    },
    focus: {
      width: 2,
      offset: 2,
      style: "solid",
    },
    icons: {
      family: "lucide",
      strokeWidth: 0.8,
      size: {
        s: 18,
        m: 20,
        l: 24,
      },
    },
    layers: {
      menu: 10250,
      snackbar: 10350,
      tooltip: 10400,
    },
    stroke: {
      outline: 1,
      focusRing: 2,
    },
    opacity: {
      scale: {
        "10": 0.1,
        "20": 0.2,
        "30": 0.3,
        "40": 0.4,
        "50": 0.5,
        "60": 0.6,
        "70": 0.7,
        "80": 0.8,
        "90": 0.9,
      },
      text: 0.8,
      lowText: 0.6,
      overlay: 0.7,
      states: {
        hover: 0.25,
        selected: 0.35,
        active: 0.45,
        disable: 0.3,
      },
    },
    shadows: [
      "none",
      "0px 2px 4px 0px #5555554D",
      "0px 4px 4px 0px #5555554D",
      "0px 4px 8px 0px #5555554D",
      "0px 4px 8px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
      "0px 4px 12px 4px #5555554D",
    ],
    duration: {
      fast: 120,
      base: 180,
      slow: 260,
    },
  } as Primitivos,
  light: {
    page: {
      bg: "#EFF6FE",
      on: "#000510",
      onMuted: "#06457F",
      border: "#6399D5",
      hover: "#B9D0E6",
    },
    raised: {
      bg: "#FFFFFF",
      on: "#000510",
      onMuted: "#06457F",
      border: "#6399D5",
      hover: "#C5D6E7",
      borderStrong: "#000510",
    },
    sunken: {
      bg: "#EFF6FE",
      on: "#000510",
      onMuted: "#06457F",
      border: "#6399D5",
      hover: "#B9D0E6",
    },
    inverse: {
      bg: "#000510",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#000510",
      hover: "#000510",
    },
    primary: {
      bg: "#185C9F",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#06457F",
      hover: "#4A80B6",
      subtle: "#E0EDFC",
      onSubtle: "#06457F",
    },
    secondary: {
      bg: "#002E59",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#06457F",
      hover: "#385E82",
      subtle: "#E0EDFC",
      onSubtle: "#06457F",
    },
    error: {
      bg: "#D31010",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#B00C0C",
      hover: "#DE4545",
      subtle: "#FFE5E5",
      onSubtle: "#B00C0C",
    },
    warning: {
      bg: "#A36700",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#855200",
      hover: "#BA8A37",
      subtle: "#FFF4DD",
      onSubtle: "#855200",
    },
    success: {
      bg: "#007B1B",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#006616",
      hover: "#399C4F",
      subtle: "#E5FFEC",
      onSubtle: "#006616",
    },
    info: {
      bg: "#003FC8",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#0032A3",
      hover: "#396BD6",
      subtle: "#E5EEFF",
      onSubtle: "#0032A3",
    },
    appBar: {
      bg: "#FFFFFF",
      on: "#000510",
      onMuted: "#06457F",
      border: "#6399D5",
      hover: "#C5D6E7",
    },
    sidebar: {
      bg: "#FFFFFF",
      on: "#000510",
      onMuted: "#06457F",
      border: "#6399D5",
      hover: "#C5D6E7",
      subtle: "#AEC6DD",
      selected: "#AEC6DD",
      onSelected: "#000510",
    },
    sensitive: {
      masked: {
        bg: "#E0EDFC",
        on: "#06457F",
        onMuted: "#06457F",
        border: "#6399D5",
        hover: "#AEC9E5",
      },
      revealed: {
        bg: "#FFF4DD",
        on: "#855200",
        onMuted: "#855200",
        border: "#FFA61F",
        hover: "#E8D1A6",
      },
      ownership: {
        bg: "#E5EEFF",
        on: "#0032A3",
        onMuted: "#0032A3",
        border: "#5E9CFF",
        hover: "#ACC2F1",
      },
      forbidden: {
        bg: "#E0EDFC",
        on: "#06457F",
        onMuted: "#06457F",
        border: "#000510",
        hover: "#AEC9E5",
      },
    },
    text: {
      primary: "#000510",
      secondary: "#06457F",
      disabled: "#A7AEB7",
    },
    divider: "#6399D5",
    focusRing: "#8F7A03",
    families: {
      grid: {
        border: "#B3B4B7",
        headBorder: "#A7AEB7",
        headBg: "#EFF6FE",
        rowHover: "#B9D0E6",
        rowAlt: "#DCE8F3",
      },
      field: {
        border: "#808288",
        borderHover: "#595D64",
        borderFocus: "#185C9F",
      },
    },
  } as SemanticSet,
  dark: {
    page: {
      bg: "#001B38",
      on: "#FFFFFF",
      onMuted: "#BDD7F5",
      border: "#185C9F",
      hover: "#0E3259",
    },
    raised: {
      bg: "#000510",
      on: "#FFFFFF",
      onMuted: "#BDD7F5",
      border: "#185C9F",
      hover: "#0E223B",
      borderStrong: "#FFFFFF",
    },
    sunken: {
      bg: "#001B38",
      on: "#FFFFFF",
      onMuted: "#BDD7F5",
      border: "#185C9F",
      hover: "#0E3259",
    },
    inverse: {
      bg: "#FFFFFF",
      on: "#000510",
      onMuted: "#000510",
      border: "#FFFFFF",
      hover: "#FFFFFF",
    },
    primary: {
      bg: "#3677BC",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#BDD7F5",
      hover: "#2965A3",
      subtle: "#002E59",
      onSubtle: "#BDD7F5",
    },
    secondary: {
      bg: "#06457F",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#BDD7F5",
      hover: "#053F76",
      subtle: "#002E59",
      onSubtle: "#BDD7F5",
    },
    error: {
      bg: "#E91717",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#FFC7C7",
      hover: "#C91212",
      subtle: "#690202",
      onSubtle: "#FFC7C7",
    },
    warning: {
      bg: "#D98800",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#FFDB8F",
      hover: "#B47100",
      subtle: "#462B00",
      onSubtle: "#FFDB8F",
    },
    success: {
      bg: "#0A9B3A",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#BFEECD",
      hover: "#08842F",
      subtle: "#003D0D",
      onSubtle: "#BFEECD",
    },
    info: {
      bg: "#1362EB",
      on: "#FFFFFF",
      onMuted: "#FFFFFF",
      border: "#C2D8FF",
      hover: "#0E4FC4",
      subtle: "#00174E",
      onSubtle: "#C2D8FF",
    },
    appBar: {
      bg: "#000510",
      on: "#FFFFFF",
      onMuted: "#BDD7F5",
      border: "#185C9F",
      hover: "#0E223B",
    },
    sidebar: {
      bg: "#000510",
      on: "#FFFFFF",
      onMuted: "#BDD7F5",
      border: "#185C9F",
      hover: "#0E223B",
      subtle: "#132D4C",
      selected: "#132D4C",
      onSelected: "#FFFFFF",
    },
    sensitive: {
      masked: {
        bg: "#002E59",
        on: "#BDD7F5",
        onMuted: "#BDD7F5",
        border: "#185C9F",
        hover: "#0E4072",
      },
      revealed: {
        bg: "#462B00",
        on: "#FFDB8F",
        onMuted: "#FFDB8F",
        border: "#A36700",
        hover: "#6B4200",
      },
      ownership: {
        bg: "#00174E",
        on: "#C2D8FF",
        onMuted: "#C2D8FF",
        border: "#003FC8",
        hover: "#052A75",
      },
      forbidden: {
        bg: "#002E59",
        on: "#BDD7F5",
        onMuted: "#BDD7F5",
        border: "#FFFFFF",
        hover: "#0E4072",
      },
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#BDD7F5",
      disabled: "#4D5F74",
    },
    divider: "#185C9F",
    focusRing: "#8F7A03",
    families: {
      grid: {
        border: "#4D5058",
        headBorder: "#4D5F74",
        headBg: "#001B38",
        rowHover: "#0E3259",
        rowAlt: "#071C35",
      },
      field: {
        border: "#808288",
        borderHover: "#A6A8AB",
        borderFocus: "#3677BC",
      },
    },
  } as SemanticSet,
  audience: {
    systemOwner: true,
  },
  systemOwnerTenant: "AGILMIND",
};

/**
 * Las capas del tema en pruebas — hermana de `capasMatu`, no una clave suya.
 *
 * Va aparte y no dentro de `previewTokens` porque ese objeto es lo que
 * `themeOptions` intercambia por `tokens` para construir la pantalla: las capas
 * no participan de esa construcción, y meterlas ahí obligaría a los dos lados
 * del intercambio a tener la misma forma sin ninguna razón. El par
 * `tokens`/`capasMatu` ya se lee así.
 *
 * Nulo por los mismos dos motivos que `capasMatu`: sin tema en pruebas, o con
 * uno que no viene de un esquema tokenizado.
 */
export const capasMatuEnPruebas: CapasMatu | null = {
  selection: {
    colorScheme: "Marca del proyecto",
    brand: "#2B6CB0",
    theme: "Cated",
    surfaces: "Mantle",
  },
  baseColors: {
    Color_0: "#FFFFFF",
    Color_50: "#EFF6FE",
    Color_100: "#E0EDFC",
    Color_200: "#BDD7F5",
    Color_300: "#92BBEB",
    Color_400: "#6399D5",
    Color_500: "#3677BC",
    Color_600: "#185C9F",
    Color_700: "#06457F",
    Color_800: "#002E59",
    Color_900: "#001B38",
    Color_950: "#000D21",
    Color_1000: "#000510",
  },
  states: {
    hover: 0.25,
    selected: 0.35,
    active: 0.45,
    disable: 0.3,
  },
  light: {
    contrast: {
      "Canvas High": {
        Canvas: "#FFFFFF",
        Content: "#000510",
        Main: "#185C9F",
        On_Main: "#FFFFFF",
        Surface: "#E0EDFC",
        On_Surface: "#06457F",
        Mantle: "#EFF6FE",
        Border: "#6399D5",
        Content_Low: "#06457F",
        Focus: "#8F7A03",
      },
      "Canvas Low": {
        Canvas: "#FFFFFF",
        Content: "#185C9F",
        Main: "#185C9F",
        On_Main: "#FFFFFF",
        Surface: "#E0EDFC",
        On_Surface: "#06457F",
        Mantle: "#EFF6FE",
        Border: "#6399D5",
        Content_Low: "#185C9F",
        Focus: "#8F7A03",
      },
      "Mantle High": {
        Canvas: "#EFF6FE",
        Content: "#000510",
        Main: "#185C9F",
        On_Main: "#FFFFFF",
        Surface: "#E0EDFC",
        On_Surface: "#000510",
        Mantle: "#FFFFFF",
        Border: "#6399D5",
        Content_Low: "#06457F",
        Focus: "#8F7A03",
      },
      "Mantle Low": {
        Canvas: "#EFF6FE",
        Content: "#185C9F",
        Main: "#185C9F",
        On_Main: "#FFFFFF",
        Surface: "#E0EDFC",
        On_Surface: "#06457F",
        Mantle: "#FFFFFF",
        Border: "#6399D5",
        Content_Low: "#185C9F",
        Focus: "#8F7A03",
      },
      Solid: {
        Canvas: "#185C9F",
        Content: "#FFFFFF",
        Main: "#E0EDFC",
        On_Main: "#06457F",
        Surface: "#002E59",
        On_Surface: "#FFFFFF",
        Mantle: "#06457F",
        Border: "#FFFFFF",
        Content_Low: "#FFFFFF",
        Focus: "#F8E059",
      },
      Highest: {
        Canvas: "#FFFFFF",
        Content: "#000510",
        Main: "#000510",
        On_Main: "#FFFFFF",
        Surface: "#000510",
        On_Surface: "#FFFFFF",
        Mantle: "#FFFFFF",
        Border: "#000510",
        Content_Low: "#000510",
        Focus: "#F8E059",
      },
    },
  },
  dark: {
    contrast: {
      "Canvas High": {
        Canvas: "#000510",
        Content: "#FFFFFF",
        Main: "#3677BC",
        On_Main: "#FFFFFF",
        Surface: "#002E59",
        On_Surface: "#BDD7F5",
        Mantle: "#001B38",
        Border: "#185C9F",
        Content_Low: "#BDD7F5",
        Focus: "#8F7A03",
      },
      "Canvas Low": {
        Canvas: "#000510",
        Content: "#3677BC",
        Main: "#3677BC",
        On_Main: "#FFFFFF",
        Surface: "#002E59",
        On_Surface: "#BDD7F5",
        Mantle: "#001B38",
        Border: "#185C9F",
        Content_Low: "#3677BC",
        Focus: "#8F7A03",
      },
      "Mantle High": {
        Canvas: "#001B38",
        Content: "#FFFFFF",
        Main: "#3677BC",
        On_Main: "#FFFFFF",
        Surface: "#002E59",
        On_Surface: "#FFFFFF",
        Mantle: "#000510",
        Border: "#185C9F",
        Content_Low: "#BDD7F5",
        Focus: "#8F7A03",
      },
      "Mantle Low": {
        Canvas: "#001B38",
        Content: "#3677BC",
        Main: "#3677BC",
        On_Main: "#FFFFFF",
        Surface: "#002E59",
        On_Surface: "#BDD7F5",
        Mantle: "#000510",
        Border: "#185C9F",
        Content_Low: "#3677BC",
        Focus: "#8F7A03",
      },
      Solid: {
        Canvas: "#3677BC",
        Content: "#FFFFFF",
        Main: "#002E59",
        On_Main: "#185C9F",
        Surface: "#06457F",
        On_Surface: "#FFFFFF",
        Mantle: "#BDD7F5",
        Border: "#FFFFFF",
        Content_Low: "#FFFFFF",
        Focus: "#F8E059",
      },
      Highest: {
        Canvas: "#000510",
        Content: "#FFFFFF",
        Main: "#FFFFFF",
        On_Main: "#000510",
        Surface: "#FFFFFF",
        On_Surface: "#000510",
        Mantle: "#000510",
        Border: "#FFFFFF",
        Content_Low: "#FFFFFF",
        Focus: "#F8E059",
      },
    },
  },
  status: {
    error: {
      light: {
        contrast: {
          "Canvas High": {
            Canvas: "#FFFFFF",
            Content: "#070000",
            Main: "#D31010",
            On_Main: "#FFFFFF",
            Surface: "#FFE5E5",
            On_Surface: "#B00C0C",
            Mantle: "#FFF3F3",
            Border: "#FF6363",
            Content_Low: "#B00C0C",
            Focus: "#8F7A03",
          },
          "Canvas Low": {
            Canvas: "#FFFFFF",
            Content: "#D31010",
            Main: "#D31010",
            On_Main: "#FFFFFF",
            Surface: "#FFE5E5",
            On_Surface: "#B00C0C",
            Mantle: "#FFF3F3",
            Border: "#FF6363",
            Content_Low: "#D31010",
            Focus: "#8F7A03",
          },
          "Mantle High": {
            Canvas: "#FFF3F3",
            Content: "#070000",
            Main: "#D31010",
            On_Main: "#FFFFFF",
            Surface: "#FFE5E5",
            On_Surface: "#070000",
            Mantle: "#FFFFFF",
            Border: "#FF6363",
            Content_Low: "#B00C0C",
            Focus: "#8F7A03",
          },
          "Mantle Low": {
            Canvas: "#FFF3F3",
            Content: "#D31010",
            Main: "#D31010",
            On_Main: "#FFFFFF",
            Surface: "#FFE5E5",
            On_Surface: "#B00C0C",
            Mantle: "#FFFFFF",
            Border: "#FF6363",
            Content_Low: "#D31010",
            Focus: "#8F7A03",
          },
          Solid: {
            Canvas: "#D31010",
            Content: "#FFFFFF",
            Main: "#FFE5E5",
            On_Main: "#B00C0C",
            Surface: "#690202",
            On_Surface: "#FFFFFF",
            Mantle: "#B00C0C",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
          Highest: {
            Canvas: "#FFFFFF",
            Content: "#070000",
            Main: "#070000",
            On_Main: "#FFFFFF",
            Surface: "#070000",
            On_Surface: "#FFFFFF",
            Mantle: "#FFFFFF",
            Border: "#070000",
            Content_Low: "#070000",
            Focus: "#F8E059",
          },
        },
      },
      dark: {
        contrast: {
          "Canvas High": {
            Canvas: "#070000",
            Content: "#FFFFFF",
            Main: "#E91717",
            On_Main: "#FFFFFF",
            Surface: "#690202",
            On_Surface: "#FFC7C7",
            Mantle: "#350000",
            Border: "#D31010",
            Content_Low: "#FFC7C7",
            Focus: "#8F7A03",
          },
          "Canvas Low": {
            Canvas: "#070000",
            Content: "#E91717",
            Main: "#E91717",
            On_Main: "#FFFFFF",
            Surface: "#690202",
            On_Surface: "#FFC7C7",
            Mantle: "#350000",
            Border: "#D31010",
            Content_Low: "#E91717",
            Focus: "#8F7A03",
          },
          "Mantle High": {
            Canvas: "#350000",
            Content: "#FFFFFF",
            Main: "#E91717",
            On_Main: "#FFFFFF",
            Surface: "#690202",
            On_Surface: "#FFFFFF",
            Mantle: "#070000",
            Border: "#D31010",
            Content_Low: "#FFC7C7",
            Focus: "#8F7A03",
          },
          "Mantle Low": {
            Canvas: "#350000",
            Content: "#E91717",
            Main: "#E91717",
            On_Main: "#FFFFFF",
            Surface: "#690202",
            On_Surface: "#FFC7C7",
            Mantle: "#070000",
            Border: "#D31010",
            Content_Low: "#E91717",
            Focus: "#8F7A03",
          },
          Solid: {
            Canvas: "#E91717",
            Content: "#FFFFFF",
            Main: "#690202",
            On_Main: "#D31010",
            Surface: "#B00C0C",
            On_Surface: "#FFFFFF",
            Mantle: "#FFC7C7",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
          Highest: {
            Canvas: "#070000",
            Content: "#FFFFFF",
            Main: "#FFFFFF",
            On_Main: "#070000",
            Surface: "#FFFFFF",
            On_Surface: "#070000",
            Mantle: "#070000",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
        },
      },
    },
    warning: {
      light: {
        contrast: {
          "Canvas High": {
            Canvas: "#FFFFFF",
            Content: "#050300",
            Main: "#A36700",
            On_Main: "#FFFFFF",
            Surface: "#FFF4DD",
            On_Surface: "#855200",
            Mantle: "#FFF9EE",
            Border: "#FFA61F",
            Content_Low: "#855200",
            Focus: "#0055AC",
          },
          "Canvas Low": {
            Canvas: "#FFFFFF",
            Content: "#A36700",
            Main: "#A36700",
            On_Main: "#FFFFFF",
            Surface: "#FFF4DD",
            On_Surface: "#855200",
            Mantle: "#FFF9EE",
            Border: "#FFA61F",
            Content_Low: "#A36700",
            Focus: "#0055AC",
          },
          "Mantle High": {
            Canvas: "#FFF9EE",
            Content: "#050300",
            Main: "#A36700",
            On_Main: "#FFFFFF",
            Surface: "#FFF4DD",
            On_Surface: "#050300",
            Mantle: "#FFFFFF",
            Border: "#FFA61F",
            Content_Low: "#855200",
            Focus: "#0055AC",
          },
          "Mantle Low": {
            Canvas: "#FFF9EE",
            Content: "#A36700",
            Main: "#A36700",
            On_Main: "#FFFFFF",
            Surface: "#FFF4DD",
            On_Surface: "#855200",
            Mantle: "#FFFFFF",
            Border: "#FFA61F",
            Content_Low: "#A36700",
            Focus: "#0055AC",
          },
          Solid: {
            Canvas: "#A36700",
            Content: "#FFFFFF",
            Main: "#FFF4DD",
            On_Main: "#855200",
            Surface: "#462B00",
            On_Surface: "#FFFFFF",
            Mantle: "#855200",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#A2D0FF",
          },
          Highest: {
            Canvas: "#FFFFFF",
            Content: "#050300",
            Main: "#050300",
            On_Main: "#FFFFFF",
            Surface: "#050300",
            On_Surface: "#FFFFFF",
            Mantle: "#FFFFFF",
            Border: "#050300",
            Content_Low: "#050300",
            Focus: "#A2D0FF",
          },
        },
      },
      dark: {
        contrast: {
          "Canvas High": {
            Canvas: "#050300",
            Content: "#FFFFFF",
            Main: "#D98800",
            On_Main: "#FFFFFF",
            Surface: "#462B00",
            On_Surface: "#FFDB8F",
            Mantle: "#291900",
            Border: "#A36700",
            Content_Low: "#FFDB8F",
            Focus: "#0055AC",
          },
          "Canvas Low": {
            Canvas: "#050300",
            Content: "#D98800",
            Main: "#D98800",
            On_Main: "#FFFFFF",
            Surface: "#462B00",
            On_Surface: "#FFDB8F",
            Mantle: "#291900",
            Border: "#A36700",
            Content_Low: "#D98800",
            Focus: "#0055AC",
          },
          "Mantle High": {
            Canvas: "#291900",
            Content: "#FFFFFF",
            Main: "#D98800",
            On_Main: "#FFFFFF",
            Surface: "#462B00",
            On_Surface: "#FFFFFF",
            Mantle: "#050300",
            Border: "#A36700",
            Content_Low: "#FFDB8F",
            Focus: "#0055AC",
          },
          "Mantle Low": {
            Canvas: "#291900",
            Content: "#D98800",
            Main: "#D98800",
            On_Main: "#FFFFFF",
            Surface: "#462B00",
            On_Surface: "#FFDB8F",
            Mantle: "#050300",
            Border: "#A36700",
            Content_Low: "#D98800",
            Focus: "#0055AC",
          },
          Solid: {
            Canvas: "#D98800",
            Content: "#FFFFFF",
            Main: "#462B00",
            On_Main: "#A36700",
            Surface: "#855200",
            On_Surface: "#FFFFFF",
            Mantle: "#FFDB8F",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#A2D0FF",
          },
          Highest: {
            Canvas: "#050300",
            Content: "#FFFFFF",
            Main: "#FFFFFF",
            On_Main: "#050300",
            Surface: "#FFFFFF",
            On_Surface: "#050300",
            Mantle: "#050300",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#A2D0FF",
          },
        },
      },
    },
    success: {
      light: {
        contrast: {
          "Canvas High": {
            Canvas: "#FFFFFF",
            Content: "#000A02",
            Main: "#007B1B",
            On_Main: "#FFFFFF",
            Surface: "#E5FFEC",
            On_Surface: "#006616",
            Mantle: "#F0FFF4",
            Border: "#5BCF83",
            Content_Low: "#006616",
            Focus: "#8F7A03",
          },
          "Canvas Low": {
            Canvas: "#FFFFFF",
            Content: "#007B1B",
            Main: "#007B1B",
            On_Main: "#FFFFFF",
            Surface: "#E5FFEC",
            On_Surface: "#006616",
            Mantle: "#F0FFF4",
            Border: "#5BCF83",
            Content_Low: "#007B1B",
            Focus: "#8F7A03",
          },
          "Mantle High": {
            Canvas: "#F0FFF4",
            Content: "#000A02",
            Main: "#007B1B",
            On_Main: "#FFFFFF",
            Surface: "#E5FFEC",
            On_Surface: "#000A02",
            Mantle: "#FFFFFF",
            Border: "#5BCF83",
            Content_Low: "#006616",
            Focus: "#8F7A03",
          },
          "Mantle Low": {
            Canvas: "#F0FFF4",
            Content: "#007B1B",
            Main: "#007B1B",
            On_Main: "#FFFFFF",
            Surface: "#E5FFEC",
            On_Surface: "#006616",
            Mantle: "#FFFFFF",
            Border: "#5BCF83",
            Content_Low: "#007B1B",
            Focus: "#8F7A03",
          },
          Solid: {
            Canvas: "#007B1B",
            Content: "#FFFFFF",
            Main: "#E5FFEC",
            On_Main: "#006616",
            Surface: "#003D0D",
            On_Surface: "#FFFFFF",
            Mantle: "#006616",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
          Highest: {
            Canvas: "#FFFFFF",
            Content: "#000A02",
            Main: "#000A02",
            On_Main: "#FFFFFF",
            Surface: "#000A02",
            On_Surface: "#FFFFFF",
            Mantle: "#FFFFFF",
            Border: "#000A02",
            Content_Low: "#000A02",
            Focus: "#F8E059",
          },
        },
      },
      dark: {
        contrast: {
          "Canvas High": {
            Canvas: "#000A02",
            Content: "#FFFFFF",
            Main: "#0A9B3A",
            On_Main: "#FFFFFF",
            Surface: "#003D0D",
            On_Surface: "#BFEECD",
            Mantle: "#002008",
            Border: "#007B1B",
            Content_Low: "#BFEECD",
            Focus: "#8F7A03",
          },
          "Canvas Low": {
            Canvas: "#000A02",
            Content: "#0A9B3A",
            Main: "#0A9B3A",
            On_Main: "#FFFFFF",
            Surface: "#003D0D",
            On_Surface: "#BFEECD",
            Mantle: "#002008",
            Border: "#007B1B",
            Content_Low: "#0A9B3A",
            Focus: "#8F7A03",
          },
          "Mantle High": {
            Canvas: "#002008",
            Content: "#FFFFFF",
            Main: "#0A9B3A",
            On_Main: "#FFFFFF",
            Surface: "#003D0D",
            On_Surface: "#FFFFFF",
            Mantle: "#000A02",
            Border: "#007B1B",
            Content_Low: "#BFEECD",
            Focus: "#8F7A03",
          },
          "Mantle Low": {
            Canvas: "#002008",
            Content: "#0A9B3A",
            Main: "#0A9B3A",
            On_Main: "#FFFFFF",
            Surface: "#003D0D",
            On_Surface: "#BFEECD",
            Mantle: "#000A02",
            Border: "#007B1B",
            Content_Low: "#0A9B3A",
            Focus: "#8F7A03",
          },
          Solid: {
            Canvas: "#0A9B3A",
            Content: "#FFFFFF",
            Main: "#003D0D",
            On_Main: "#007B1B",
            Surface: "#006616",
            On_Surface: "#FFFFFF",
            Mantle: "#BFEECD",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
          Highest: {
            Canvas: "#000A02",
            Content: "#FFFFFF",
            Main: "#FFFFFF",
            On_Main: "#000A02",
            Surface: "#FFFFFF",
            On_Surface: "#000A02",
            Mantle: "#000A02",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
        },
      },
    },
    info: {
      light: {
        contrast: {
          "Canvas High": {
            Canvas: "#FFFFFF",
            Content: "#000207",
            Main: "#003FC8",
            On_Main: "#FFFFFF",
            Surface: "#E5EEFF",
            On_Surface: "#0032A3",
            Mantle: "#F2F6FF",
            Border: "#5E9CFF",
            Content_Low: "#0032A3",
            Focus: "#8F7A03",
          },
          "Canvas Low": {
            Canvas: "#FFFFFF",
            Content: "#003FC8",
            Main: "#003FC8",
            On_Main: "#FFFFFF",
            Surface: "#E5EEFF",
            On_Surface: "#0032A3",
            Mantle: "#F2F6FF",
            Border: "#5E9CFF",
            Content_Low: "#003FC8",
            Focus: "#8F7A03",
          },
          "Mantle High": {
            Canvas: "#F2F6FF",
            Content: "#000207",
            Main: "#003FC8",
            On_Main: "#FFFFFF",
            Surface: "#E5EEFF",
            On_Surface: "#000207",
            Mantle: "#FFFFFF",
            Border: "#5E9CFF",
            Content_Low: "#0032A3",
            Focus: "#8F7A03",
          },
          "Mantle Low": {
            Canvas: "#F2F6FF",
            Content: "#003FC8",
            Main: "#003FC8",
            On_Main: "#FFFFFF",
            Surface: "#E5EEFF",
            On_Surface: "#0032A3",
            Mantle: "#FFFFFF",
            Border: "#5E9CFF",
            Content_Low: "#003FC8",
            Focus: "#8F7A03",
          },
          Solid: {
            Canvas: "#003FC8",
            Content: "#FFFFFF",
            Main: "#E5EEFF",
            On_Main: "#0032A3",
            Surface: "#00174E",
            On_Surface: "#FFFFFF",
            Mantle: "#0032A3",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
          Highest: {
            Canvas: "#FFFFFF",
            Content: "#000207",
            Main: "#000207",
            On_Main: "#FFFFFF",
            Surface: "#000207",
            On_Surface: "#FFFFFF",
            Mantle: "#FFFFFF",
            Border: "#000207",
            Content_Low: "#000207",
            Focus: "#F8E059",
          },
        },
      },
      dark: {
        contrast: {
          "Canvas High": {
            Canvas: "#000207",
            Content: "#FFFFFF",
            Main: "#1362EB",
            On_Main: "#FFFFFF",
            Surface: "#00174E",
            On_Surface: "#C2D8FF",
            Mantle: "#000C25",
            Border: "#003FC8",
            Content_Low: "#C2D8FF",
            Focus: "#8F7A03",
          },
          "Canvas Low": {
            Canvas: "#000207",
            Content: "#1362EB",
            Main: "#1362EB",
            On_Main: "#FFFFFF",
            Surface: "#00174E",
            On_Surface: "#C2D8FF",
            Mantle: "#000C25",
            Border: "#003FC8",
            Content_Low: "#1362EB",
            Focus: "#8F7A03",
          },
          "Mantle High": {
            Canvas: "#000C25",
            Content: "#FFFFFF",
            Main: "#1362EB",
            On_Main: "#FFFFFF",
            Surface: "#00174E",
            On_Surface: "#FFFFFF",
            Mantle: "#000207",
            Border: "#003FC8",
            Content_Low: "#C2D8FF",
            Focus: "#8F7A03",
          },
          "Mantle Low": {
            Canvas: "#000C25",
            Content: "#1362EB",
            Main: "#1362EB",
            On_Main: "#FFFFFF",
            Surface: "#00174E",
            On_Surface: "#C2D8FF",
            Mantle: "#000207",
            Border: "#003FC8",
            Content_Low: "#1362EB",
            Focus: "#8F7A03",
          },
          Solid: {
            Canvas: "#1362EB",
            Content: "#FFFFFF",
            Main: "#00174E",
            On_Main: "#003FC8",
            Surface: "#0032A3",
            On_Surface: "#FFFFFF",
            Mantle: "#C2D8FF",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
          Highest: {
            Canvas: "#000207",
            Content: "#FFFFFF",
            Main: "#FFFFFF",
            On_Main: "#000207",
            Surface: "#FFFFFF",
            On_Surface: "#000207",
            Mantle: "#000207",
            Border: "#FFFFFF",
            Content_Low: "#FFFFFF",
            Focus: "#F8E059",
          },
        },
      },
    },
  },
};
